/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {Fragment} from 'react';
import {FormattedMessage} from 'react-intl';
import PropTypes from 'prop-types';

import {AlertIcon} from '@salesforce/retail-react-app/app/components/icons';
import PasswordlessLogin from '@salesforce/retail-react-app/app/components/passwordless-login';
import {Alert, Stack, Text, useMultiStyleConfig} from '@salesforce/retail-react-app/app/components/shared/ui';
import StandardLogin from '@salesforce/retail-react-app/app/components/standard-login';
import WhySignUp from '@amblique/king-living/app/components/why-sign-up';

const LoginForm = ({
  submitForm,
  handleForgotPasswordClick,
  handlePasswordlessLoginClick,
  form,
  isPasswordlessEnabled = false,
  isSocialEnabled = false,
  idps = [],
  setLoginType,
}) => {
  const styles = useMultiStyleConfig('FormAuth', {});

  return (
    <Fragment>
      <Stack {...styles.header}>
        <Text {...styles.title}>
          <FormattedMessage
            defaultMessage="Sign up or login"
            id="login_page.title"
          />
        </Text>
        <Text {...styles.subtitle}>
          <FormattedMessage
            defaultMessage="Welcome back, {name}!"
            id="login_page.subtitle"
            values={{name: 'John Doe'}} // TODO: Replace with actual name by Backend
          />
        </Text>
        <Text {...styles.description}>
          <FormattedMessage
            defaultMessage="You will need to verify your email address"
            id="login_page.login.description"
          />
        </Text>
      </Stack>
      <form
        data-testid="sf-auth-modal-form"
        id="login-form"
        onSubmit={form.handleSubmit(submitForm)}
      >
        {form.formState.errors?.global && (
          <Alert marginBottom={8} status="error">
            <AlertIcon boxSize={4} color="red.500" />
            <Text fontSize="sm" ml={3}>
              {form.formState.errors.global.message}
            </Text>
          </Alert>
        )}
        <Stack>
          {isPasswordlessEnabled ? (
            <PasswordlessLogin
              form={form}
              handleForgotPasswordClick={handleForgotPasswordClick}
              handlePasswordlessLoginClick={handlePasswordlessLoginClick}
              idps={idps}
              isSocialEnabled={isSocialEnabled}
              setLoginType={setLoginType}
            />
          ) : (
            <StandardLogin
              form={form}
              handleForgotPasswordClick={handleForgotPasswordClick}
              idps={idps}
              isSocialEnabled={isSocialEnabled}
            />
          )}
        </Stack>
      </form>
      <WhySignUp />
    </Fragment>
  );
};

LoginForm.propTypes = {
  submitForm: PropTypes.func,
  handleForgotPasswordClick: PropTypes.func,
  handlePasswordlessLoginClick: PropTypes.func,
  form: PropTypes.object,
  isPasswordlessEnabled: PropTypes.bool,
  isSocialEnabled: PropTypes.bool,
  idps: PropTypes.arrayOf(PropTypes.string),
  setLoginType: PropTypes.func,
};

export default LoginForm;
